import { Action } from '@reduxjs/toolkit';
import classNames from 'classnames';
import React from 'react';
import { Dispatch } from 'redux';

import { get } from '@eika-infrastruktur/request';
import { RequestException } from '@eika-infrastruktur/request/src/exceptions';
import { Input, InputLabel } from '@eika/input';
import { ErrorLabel } from '@eika/label';
import { Loader } from '@eika/loader';

import { InputType } from '../../components/input/input-text';
import { failure, loading, notAsked, RemoteData, RemoteDataType, success } from '../../reducers/remoteData';
import { PoststedDTO, TextInputSize, Valideringstype } from '../../types/generated-types';
import { hentPoststedUrl } from '../../urler';
import { updatePostnummerAndPoststed } from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import { getFeilmeldingFrom } from '../reducer/valideringstype';
import { validatePostnummer } from '../validation/validate';
import { CurrentPostnummersporsmal } from './postnummer-types';

import './postnummer.scss';

interface PoststedProps {
    poststed: RemoteData<string>;
}

const Poststed = ({ poststed }: PoststedProps) => {
    switch (poststed.type) {
        case RemoteDataType.LOADING:
            return (
                <div className="poststed-margin">
                    <Loader ariaLabel="Henter poststed" mini />
                </div>
            );
        case RemoteDataType.SUCCESS:
            return <span className="poststed-margin">{poststed.data}</span>;
        case RemoteDataType.FAILURE:
            if (poststed.exception.status === 404) {
                return <ErrorLabel className="sgw-select__error-label" text={poststed.exception.message} />;
            }
            return null;
        default:
            return null;
    }
};

const isValid = (currentSporsmal: CurrentPostnummersporsmal, showFeilmelding: boolean) => {
    if (!validatePostnummer(currentSporsmal.postnummer) && showFeilmelding) {
        return getFeilmeldingFrom(Valideringstype.Postnummer, currentSporsmal.postnummer, TextInputSize.ExtraSmall);
    }
    return null;
};

const getPoststed = (postnummer: string) => async (dispatch: Dispatch<Action>) => {
    get(hentPoststedUrl(postnummer))
        .then((response: PoststedDTO) => {
            dispatch(updatePostnummerAndPoststed(postnummer, success(response.value)));
        })
        .catch((exception: RequestException) => {
            dispatch(updatePostnummerAndPoststed(postnummer, failure(exception)));
        });
};

interface PostnummerProps {
    currentSporsmal: CurrentPostnummersporsmal;
    showFeilmelding: boolean;
}

export const Postnummer = ({ currentSporsmal, showFeilmelding }: PostnummerProps) => {
    const dispatch = useSporsmalsflytDispatch();

    const onChange = (postnummer: string) => {
        if (validatePostnummer(postnummer)) {
            dispatch(updatePostnummerAndPoststed(postnummer, loading()));
            dispatch(getPoststed(postnummer));
        } else {
            dispatch(updatePostnummerAndPoststed(postnummer, notAsked()));
        }
    };
    const id = `postnummer-sporsmal-${currentSporsmal.sporsmal.id}`;
    const flexOrBlockClass =
        currentSporsmal.poststed.type === RemoteDataType.FAILURE
            ? `postnummer-container-block`
            : `postnummer-container-flex`;

    return (
        <div className={`container__standard-width ${flexOrBlockClass}`}>
            <InputLabel label={currentSporsmal.sporsmal.tekst} htmlFor={id}>
                <div className={classNames(`input-text-wrapper`, `input-text__${TextInputSize.ExtraSmall}`)}>
                    <Input
                        id={id}
                        type={InputType.Number}
                        autoComplete="off"
                        inputMode="numeric"
                        value={currentSporsmal.postnummer}
                        onInput={(event) => onChange(event.currentTarget.value)}
                        error={isValid(currentSporsmal, showFeilmelding)}
                    />
                </div>
            </InputLabel>
            <Poststed poststed={currentSporsmal.poststed} />
        </div>
    );
};
